import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Sip } from '@sbclite/core';
import { api } from '../services/api';
import { containerQueryKeys } from './useContainers';
import { sipPeerQueryKeys } from './useSipPeers';

interface Envelope<T> {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
  readonly pagination?: {
    readonly limit: number;
    readonly offset: number;
    readonly total: number;
    readonly totalPages: number;
  };
}

export const sipProfileQueryKeys = {
  profiles: ['sip-profiles'] as const,
  profile: (profileId: string) => ['sip-profiles', profileId] as const,
  usage: (profileId: string) => ['sip-profiles', profileId, 'usage'] as const,
  peerEffectivePolicy: (peerId: string) => ['sip-peers', peerId, 'effective-policy'] as const,
  inventory: ['sip-profiles', 'inventory'] as const,
} as const;

export interface SipProfileBackfillInventoryResponse {
  readonly rows: ReadonlyArray<Sip.SipProfileBackfillAnalysis>;
  readonly summary: Sip.SipProfileBackfillSummary;
}

export interface SipProfileBackfillResponse {
  readonly dryRun: boolean;
  readonly appliedCount: number;
  readonly createdProfileCount: number;
  readonly reusedProfileCount: number;
  readonly before: Sip.SipProfileBackfillSummary;
  readonly after: Sip.SipProfileBackfillSummary;
  readonly cleanupReady: boolean;
}

export function useSipProfiles(params?: { readonly limit?: number; readonly offset?: number }) {
  return useQuery({
    queryKey: [...sipProfileQueryKeys.profiles, params] as const,
    queryFn: async () => {
      const response = await api.get<Envelope<readonly Sip.SipProfileInfo[]>>('/api/sip-profiles', params);
      return response.data;
    },
  });
}

export function useSipProfile(profileId: string | undefined) {
  return useQuery({
    queryKey: sipProfileQueryKeys.profile(profileId ?? ''),
    queryFn: async () => {
      const response = await api.get<Envelope<Sip.SipProfileInfo>>(`/api/sip-profiles/${profileId}`);
      return response.data;
    },
    enabled: !!profileId,
  });
}

export function useCreateSipProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Sip.SipProfileCreateInput) => {
      const response = await api.post<Envelope<Sip.SipProfileInfo>>('/api/sip-profiles', input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.profiles });
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useUpdateSipProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, input }: { readonly profileId: string; readonly input: Sip.SipProfileUpdateInput }) => {
      const response = await api.put<Envelope<Sip.SipProfileInfo>>(`/api/sip-profiles/${profileId}`, input);
      return response.data;
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.profiles });
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.profile(variables.profileId) });
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useDeleteSipProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId: string) => {
      const response = await api.delete<Envelope<{ readonly id: string }>>(`/api/sip-profiles/${profileId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.profiles });
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}

export function useEffectiveSipPeerPolicy(peerId: string | undefined, containerId?: string) {
  return useQuery({
    queryKey: [...sipProfileQueryKeys.peerEffectivePolicy(peerId ?? ''), containerId ?? null] as const,
    queryFn: async () => {
      const params = containerId ? `?containerId=${encodeURIComponent(containerId)}` : '';
      const response = await api.get<Envelope<Sip.SipPeerEffectivePolicyView>>(`/api/sip-peers/${peerId}/effective-policy${params}`);
      return response.data;
    },
    enabled: !!peerId,
  });
}

export function useSipProfileInventory() {
  return useQuery({
    queryKey: sipProfileQueryKeys.inventory,
    queryFn: async () => {
      const response = await api.getSipProfileInventory();
      return response.data;
    },
  });
}

export function useRunSipProfileBackfill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (apply: boolean) => {
      const response = await api.runSipProfileBackfill(apply);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.inventory });
      queryClient.invalidateQueries({ queryKey: sipProfileQueryKeys.profiles });
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.prerequisitesRoot });
    },
  });
}
